import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import {situacoesLinha, stripLinesReportLayout} from './lines-report-options';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchSubsystems } from '../../hooks/fetch/use-fetch-subsystems';
import { useFetchVehicleTypes } from '../../hooks/fetch/use-fetch-vehicle-types';
import { useFetchLineServices } from '../../hooks/fetch/use-fetch-line-services';
import { useFetchFilteredCompaniesByLine } from '../../hooks/fetch/use-fetch-filtered-companies-by-line';

import Loading from '../../../../layouts/Loading';
import { GridItem } from '../../components/GridItem';
import { ReportSelect } from '../../components/ReportSelect';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';
import { ReportRadioGroup } from "../../components/ReportRadioGroup";
import {useFetchGrants} from "../../hooks/fetch/use-fetch-grants";
import {useFetchLocations} from "../../hooks/fetch/use-fetch-locations";

const POR_EMPRESA = 1;
const POR_LOCALIDADE = 2;
const LINHAS_CANCELADAS = 3;

const StripLinesReport = () => {
  const { handleSelectChange, handleRadioChange } = useStipReport();
  const { values, setFieldValue } = useFormikContext();

  const { loading: loadingCities, locations: cities } = useFetchLocations();
  const { loading: loadingServices, lineServices: services } = useFetchLineServices();
  const { loading: loadingVehicles, vehicleTypes } = useFetchVehicleTypes();
  const { loading: loadingSubsystems, subsystems } = useFetchSubsystems();
  const { loading: loadingLines, lines, companies } = useFetchFilteredCompaniesByLine({ lineRequest: { boSlic: false, boOrdenarPorNuLinha: true }, boStrip: true });
  const { loading: loadingGrants, grants } = useFetchGrants();

  const loading =
    loadingCities || loadingSubsystems || loadingLines || loadingVehicles || loadingServices || loadingGrants;

  const reportByCompany = values.cdTipoRelatorioLinhas === POR_EMPRESA;
  const reportByLocation = values.cdTipoRelatorioLinhas === POR_LOCALIDADE;
  const reportByCanceledLines = values.cdTipoRelatorioLinhas === LINHAS_CANCELADAS;
  const noLines = lines.length === 0;
  const linesFieldLabel = noLines ? 'Não há linhas disponíveis' : 'Todos as linhas / serviços';

  const getLabelDeRazaoSocialOrNmCompleto = option => {
    return Boolean(option.deRazaoSocial) ? option.deRazaoSocial : option.nmCompleto;
  };

  useEffect(() => {
    setFieldValue('localidades', []);
    setFieldValue('dtCadastro', { dtInicio: '', dtFim: '' });
    setFieldValue('dtVencimento', { dtInicio: '', dtFim: '' });
    // eslint-disable-next-line
  }, [values.cdTipoRelatorioLinhas]);

  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecionar tipo de relatório:</ReportSectionTitle>
        <ReportSelect
          options={stripLinesReportLayout}
          name="cdTipoRelatorioLinhas"
          onSelect={handleSelectChange}
        />
      </GridItem>

        <GridItem sm={6}>
          { reportByCompany || reportByLocation ?
            <>
              <ReportSectionTitle gutterBottom>Situação da linha:</ReportSectionTitle>
              <ReportRadioGroup name={'situacaoLinha'}
                                options={situacoesLinha}
                                onSelect={handleRadioChange}
              />
            </>
            : null
          }
        </GridItem>

      {reportByCompany && (
        <>
          <GridItem>
            <ReportSectionTitle>Filtrar por data do contrato:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtCadastro.dtInicio" label="Início" />
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtCadastro.dtFim" label="Fim" />
          </GridItem>
        </>
      )}

      {reportByCanceledLines && (
        <>
          <GridItem>
            <ReportSectionTitle>Filtrar por data de cancelamento:</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtInicio" label="Início" />
          </GridItem>
          <GridItem sm={6}>
            <ReportDatePicker name="dtVencimento.dtFim" label="Fim" />
          </GridItem>
        </>
      )}

      {reportByLocation && (
        <>
          <GridItem>
            <ReportSectionTitle>Selecione a(s) localidade(s):</ReportSectionTitle>
          </GridItem>
          <GridItem sm={6}>
            <ReportMultiAutocomplete
              options={cities}
              onSelect={handleSelectChange}
              label="Todas as localidades"
              name="localidades"
              valueProperty="deNome"
              labelProperty="deNome"
            />
          </GridItem>
        </>
      )}

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) linha(s) / serviço(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={lines}
          disabled={noLines}
          onSelect={handleSelectChange}
          label={linesFieldLabel}
          name="linhas"
          valueProperty="cdLinha"
          labelProperty="deDescricao"
          limitTags={1}
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) empresa(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={companies}
          onSelect={handleSelectChange}
          label="Todas as empresas"
          name="empresas"
          valueProperty="cdEmpresa"
          labelProperty="deRazaoSocial"
          uppercaseLabels
          getOptionLabel={getLabelDeRazaoSocialOrNmCompleto}
          limitTags={1}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione o(s) subsistema(s):</ReportSectionTitle>
        <ReportMultiSelect
          options={subsystems}
          onSelect={handleSelectChange}
          label="Todos os subsistemas"
          name="subSistemas"
          valueProperty="cdSubSistema"
          labelProperty="deSubSistema"
          selectAll
        />
      </GridItem>

      { reportByCompany ?
        <GridItem sm={6}>
          <ReportSectionTitle gutterBottom>Outorga:</ReportSectionTitle>
          <ReportMultiSelect
            options={grants}
            onSelect={handleSelectChange}
            label="Selecione outorga"
            name="outorgas"
            valueProperty="cdLinhaOutorga"
            labelProperty="deLinhaOutorga"
          />
        </GridItem>

        : null
      }

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Tipos de veículos:</ReportSectionTitle>
        <ReportMultiSelect
          options={vehicleTypes}
          onSelect={handleSelectChange}
          label="Selecione tipos de veículos"
          name="tiposVeiculo"
          valueProperty="cdTipoVeiculo"
          labelProperty="deTipoVeiculo"
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Serviços:</ReportSectionTitle>
        <ReportMultiSelect
          options={services}
          onSelect={handleSelectChange}
          label="Selecione os serviços"
          name="tiposServico"
          valueProperty="cdLinhaServico"
          labelProperty="deLinhaServico"
        />
      </GridItem>
    </GridItem>
  );
};

export default StripLinesReport;
